import { FC } from "react";
import { Bar, BarChart, LabelList, ResponsiveContainer, Tooltip, XAxis } from "recharts";
import { Card, CardContent } from "@/lib/shadcn/ui/card";
import { CalendarIcon } from "@radix-ui/react-icons";
import CustomLabel from "@/lib/molecules/barChartCustomLabel";
import ComparisonTable from "@/lib/shadcn/components/comparison-table";

interface BarChartStatsCardProps {
    metric: string;
    total: number;
    data: { name: string; value: number }[];
    dataPreviousPeriod: { name: string; value: number }[];
    dataPreviousYear: { name: string; value: number }[];
    period: string;
    showPreviousYear: boolean;
    totalPreviousPeriod: number;
    totalPreviousYear: number;
    totalValueFormatter: (value: number) => string;
    barValueFormatter: (value: number) => string;
}

const BarChartStatsCard: FC<BarChartStatsCardProps> = ({
    metric,
    total,
    data,
    dataPreviousPeriod,
    dataPreviousYear,
    period,
    showPreviousYear,
    totalPreviousPeriod,
    totalPreviousYear,
    totalValueFormatter,
    barValueFormatter,
}) => {
    return (
        <Card className="h-full">
            <CardContent className="p-2 pt-4">
                <div className="flex flex-col space-y-3">
                    {/* Header Section */}
                    <div>
                        <h2 className=" font-normal text-muted-foreground mb-0.5">Total {metric}</h2>
                        <h3 className="text-xl font-bold whitespace-nowrap" data-testid="bar-chart-total">
                            {totalValueFormatter(total)}
                        </h3>
                    </div>

                    {/* Chart Section */}
                    <div className="h-[180px] w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                                data={data.map((item) => ({ ...item, value: item.value }))}
                                margin={{ top: 0, right: 5, left: 0, bottom: 0 }}
                            >
                                <XAxis
                                    dataKey="name"
                                    tick={(props) => {
                                        const { verticalAnchor, visibleTicksCount, tickFormatter, ...rest } = props;
                                        return (
                                            <g>
                                                <text {...rest} dy={12} fontSize={13}>
                                                    {props.payload.value}
                                                </text>
                                            </g>
                                        );
                                    }}
                                />
                                <Tooltip />
                                <Bar dataKey="value" fill="#1F2937">
                                    <LabelList content={<CustomLabel barValueFormatter={barValueFormatter} fontSize={12} />} />
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>

                    {/* Period and Table Section */}
                    <div className="space-y-2">
                        <div className="flex items-center  text-muted-foreground">
                            <CalendarIcon className="mr-1.5 h-3.5 w-3.5" />
                            <span className={"text-xs"}>{period}</span>
                        </div>

                        <ComparisonTable
                            columnNames={data.map((item) => item.name)}
                            period={period}
                            showPreviousYear={showPreviousYear}
                            periodData={dataPreviousPeriod}
                            yearData={dataPreviousYear}
                            currentData={data}
                            totalCurrent={total}
                            totalPreviousPeriod={totalPreviousPeriod}
                            totalPreviousYear={totalPreviousYear}
                            valueFormatter={barValueFormatter}
                        />
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};

export default BarChartStatsCard;
